<template>
    <div>
        <table class="table table-striped table-class customer-highlight-table hidden-sm hidden-xs">
            <thead>
                <tr>
                    <th
                        v-for="(header, idx) in headers"
                        :key="idx"
                        @click="sortBy(idx, header.type)"
                        class="dashboard-sort-header"
                    >
                        <span v-if="sortColumn == idx">
                            <font-awesome-icon
                                v-if="sortDirection != 'asc'"
                                :icon="['fas', 'chevron-up']"
                            />
                            <font-awesome-icon
                                v-if="sortDirection != 'desc'"
                                :icon="['fas', 'chevron-down']"
                            />
                        </span>
                        {{ header.label }}
                    </th>
                    <th v-if="!readOnly" scope="col" class="dashboard-sort-header">ACT BTN</th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="(item, index) in paginatedData"
                    :key="getRowKey(item, index)"
                    :class="rowClass(item)"
                >
                    <td>
                        <PartNumberDisplay
                            :part-number="item.part_number"
                            :selected-part="part_selected"
                        />
                    </td>
                    <td>{{ item.description || '-' }}</td>
                    <td>
                        <span class="inventory-source-label" :class="sourceClass(item.source)">
                            {{ item.source || '-' }}
                        </span>
                    </td>
                    <td>{{ item.warehouse || '-' }}</td>
                    <td>{{ formatNumeric(item.quantity) || '-' }}</td>
                    <td>{{ item.condition || '-' }}</td>
                    <td :class="{ 'text-danger': item.unit_cost === 'NEED PRICING APPROVAL' }">
                        {{ formatInventoryUnitCost(item.unit_cost) }}
                    </td>
                    <td>{{ formatInventoryEntryDate(item.entry_date) }}</td>
                    <td v-if="!readOnly">
                        <a
                            href="javascript:;"
                            @click="toggleQuoteModal(item)"
                            title="Add Quote"
                            class="dashboard-action-link"
                        >
                            <font-awesome-icon :icon="['fas', 'plus']" />
                        </a>
                    </td>
                </tr>
            </tbody>
        </table>

        <div class="d-flex justify-content-between align-items-center my-3">
            <div>
                <label for="inventoryItemsPerPage" class="me-2">Items per page:</label>
                <select
                    id="inventoryItemsPerPage"
                    v-model="itemsPerPage"
                    @change="handleItemsPerPageChange"
                    class="form-select d-inline-block w-auto"
                >
                    <option v-for="option in [10, 20, 50, 100]" :key="option" :value="option">
                        {{ option }}
                    </option>
                </select>
            </div>

            <div>
                <button
                    class="btn btn-danger"
                    @click="changePage(currentPage - 1)"
                    :disabled="currentPage === 1"
                >
                    Previous
                </button>

                <span class="mx-3">
                    Page <strong>{{ currentPage }}</strong> of <strong>{{ totalPages }}</strong>
                </span>

                <button
                    class="btn btn-danger"
                    @click="changePage(currentPage + 1)"
                    :disabled="currentPage === totalPages"
                >
                    Next
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    import PartNumberDisplay from '@/components/shared/PartNumberDisplay.vue'
    import {
        buildDashboardRowHighlightClass,
        compareHighlightedCustomerFirst,
        compareSelectedPartFirst,
        getDashboardRowCustomerName,
    } from '@/helpers/dashboardRowHighlight.js'
    import { formatMoneyValue, formatNumeric, parseNumeric } from '@/helpers/string.js'
    import { formatComponentControlDate } from '@/helpers/dates.js'

    export default {
        name: 'InventoryTable',
        components: {
            PartNumberDisplay,
        },
        props: {
            data: {
                type: Array,
                default: () => [],
            },
            part_selected: {
                type: String,
                default: '',
            },
            highlightedCustomer: {
                type: String,
                default: '',
            },
            readOnly: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                headers: [
                    { label: 'Part Number', type: 'string' },
                    { label: 'Description', type: 'string' },
                    { label: 'Source', type: 'string' },
                    { label: 'Warehouse', type: 'string' },
                    { label: 'Quantity', type: 'numeric' },
                    { label: 'Cond', type: 'string' },
                    { label: 'Unit Cost', type: 'numeric' },
                    { label: 'Entry Date', type: 'date' },
                ],
                sortColumn: 7,
                columnType: 'date',
                sortDirection: 'desc',
                currentPage: 1,
                itemsPerPage: 10,
            }
        },
        computed: {
            sortedData() {
                return this.data.slice().sort((a, b) => {
                    const partPriority = compareSelectedPartFirst(
                        a?.part_number,
                        b?.part_number,
                        this.part_selected
                    )
                    if (partPriority !== 0) return partPriority

                    const customerPriority = compareHighlightedCustomerFirst(
                        getDashboardRowCustomerName(a),
                        getDashboardRowCustomerName(b),
                        this.highlightedCustomer
                    )
                    if (customerPriority !== 0) return customerPriority

                    const key = this.getColumnKey(this.sortColumn)
                    let valueA = a[key]
                    let valueB = b[key]

                    if (this.columnType === 'numeric') {
                        valueA = parseNumeric(valueA, 0)
                        valueB = parseNumeric(valueB, 0)
                    } else if (this.columnType === 'date') {
                        valueA = formatComponentControlDate(valueA)
                        valueB = formatComponentControlDate(valueB)
                    } else {
                        valueA = String(valueA || '').toLowerCase()
                        valueB = String(valueB || '').toLowerCase()
                    }

                    return this.sortDirection === 'asc'
                        ? valueA > valueB
                            ? 1
                            : -1
                        : valueA < valueB
                          ? 1
                          : -1
                })
            },
            paginatedData() {
                const start = (this.currentPage - 1) * this.itemsPerPage
                const end = start + this.itemsPerPage
                return this.sortedData.slice(start, end)
            },
            totalPages() {
                return Math.max(1, Math.ceil(this.data.length / this.itemsPerPage))
            },
        },
        watch: {
            data: {
                handler() {
                    this.applyDefaultSort()
                },
                immediate: true,
            },
        },
        methods: {
            formatNumeric,
            formatInventoryEntryDate(value) {
                return formatComponentControlDate(value) || '-'
            },
            formatInventoryUnitCost(value) {
                if (!value) return '-'
                return formatMoneyValue(value) || value
            },
            applyDefaultSort() {
                this.sortColumn = 7
                this.columnType = 'date'
                this.sortDirection = 'desc'
                this.currentPage = 1
            },
            sortBy(columnIndex, columnType) {
                this.columnType = columnType

                if (this.sortColumn === columnIndex) {
                    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
                } else {
                    this.sortColumn = columnIndex
                    this.sortDirection = 'asc'
                }
            },
            getColumnKey(index) {
                const keys = [
                    'part_number',
                    'description',
                    'source',
                    'warehouse',
                    'quantity',
                    'condition',
                    'unit_cost',
                    'entry_date',
                ]
                return keys[index]
            },
            changePage(page) {
                if (page >= 1 && page <= this.totalPages) {
                    this.currentPage = page
                }
            },
            handleItemsPerPageChange() {
                this.currentPage = 1
            },
            toggleQuoteModal(item) {
                this.$emit('toggleQuoteModal', {
                    ...item,
                    part_quoted: item?.part_number || '',
                    description: item?.description || '',
                })
            },
            getRowKey(item, index) {
                return [
                    item.part_number || '',
                    item.source || '',
                    item.warehouse || '',
                    item.condition || '',
                    item.entry_date || '',
                    index,
                ].join(':')
            },
            sourceClass(source) {
                if (source === 'GlobalParts Inventory') {
                    return 'inventory-source-label--global'
                }
                return 'inventory-source-label--greenwood'
            },
            rowClass(item) {
                return buildDashboardRowHighlightClass({
                    highlightedCustomer: this.highlightedCustomer,
                    customerName: getDashboardRowCustomerName(item),
                    selectedPart: this.part_selected,
                    partNumber: item?.part_number,
                })
            },
        },
    }
</script>

<style scoped>
    .inventory-source-label {
        display: inline-block;
        padding: 0.125rem 0.5rem;
        border-radius: 999px;
        font-size: 0.875rem;
        font-weight: 600;
    }

    .inventory-source-label--greenwood {
        color: var(--color-text-secondary);
    }

    .inventory-source-label--global {
        color: var(--color-state-info);
    }
</style>
